import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { Minus, Plus, ShoppingCart, Trash } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, total, storeSlug } = useCart();
  const [storeColors, setStoreColors] = useState(null);

  useEffect(() => {
    if (!storeSlug) return;
    axios.get(`${API}/api/s/${storeSlug}`).then(r => {
      const si = r.data?.identity || {};
      setStoreColors({ primary: si.primary_color || '#c2185b', secondary: si.secondary_color || '#333' });
    }).catch(() => {});
  }, [storeSlug]);

  const primary = storeColors?.primary || '#c2185b';
  const checkoutUrl = '/checkout';

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={24} style={{ color: primary }} />
            <span>Panier ({items.length} article{items.length !== 1 ? 's' : ''})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <ShoppingCart size={64} className="text-slate-200 mb-4" />
            <p className="text-slate-500 mb-2 text-[15px]">Votre panier est vide</p>
            <p className="text-slate-400 text-[12px] mb-6">Ajoutez des produits pour commencer</p>
            <button onClick={() => setIsOpen(false)}
              className="px-6 py-2.5 rounded-lg text-white text-[13px] font-semibold transition-all hover:brightness-110"
              style={{ backgroundColor: primary }}>
              Continuer mes achats
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {items.map((item, idx) => (
                <div key={`${item.product_id}-${idx}`} className="flex gap-3 p-3 bg-slate-50 rounded-xl" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-[70px] h-[70px] bg-white rounded-lg overflow-hidden shrink-0 border border-slate-100">
                    {item.image
                      ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                      : <div className="w-full h-full flex items-center justify-center"><ShoppingCart size={20} className="text-slate-300" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[13px] font-semibold text-slate-800 truncate">{item.name}</h3>
                    {item.variation && (
                      <p className="text-[11px] text-slate-400">{Object.entries(item.variation).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>
                    )}
                    <p className="text-[15px] font-bold mt-1" style={{ color: primary }}>{item.price?.toFixed(2)} €</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border rounded-lg overflow-hidden">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variation)}
                          className="p-1.5 hover:bg-slate-100 transition-colors"><Minus size={12} /></button>
                        <span className="px-3 text-[12px] font-semibold">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variation)}
                          className="p-1.5 hover:bg-slate-100 transition-colors"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => removeItem(item.product_id, item.variation)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between text-[17px] font-bold">
                <span>Total</span>
                <span style={{ color: primary }}>{total.toFixed(2)} €</span>
              </div>
              <Link to={checkoutUrl} onClick={() => setIsOpen(false)}>
                <button className="w-full py-3.5 rounded-lg text-white text-[14px] font-bold transition-all hover:brightness-110"
                  style={{ backgroundColor: primary }} data-testid="checkout-btn">
                  Passer la commande
                </button>
              </Link>
              <button className="w-full py-2.5 rounded-lg border border-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                onClick={() => setIsOpen(false)}>
                Continuer mes achats
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
